import { AxiosError } from "axios";
import type { NavigateFunction } from "react-router";

import { sessionExchangeRequest, signinPrecheckRequest } from "../api/auth";
import { setLogin } from "../api/redux-toolkit/reducer/authSlice";
import { fetchMe } from "../api/users";
import { ENFORCE_WEB_APP_CONTEXT } from "../config/auth-features";
import {
  cognitoSignOut,
  currentCognitoIdToken,
  inRequiredCognitoGroup,
} from "./cognito-auth";
import { setCsrfToken } from "./api-session";
import type { AuthDispatch } from "../stores/auth-hooks";
import { webCommonHost } from "../config/host";

interface ApiError {
  error?: { code?: string };
}

function authErrorCode(error: unknown): string | undefined {
  if (!(error instanceof AxiosError)) return undefined;
  return (error.response?.data as ApiError | undefined)?.error?.code;
}

async function runSigninPrecheck(email: string): Promise<void> {
  try {
    await signinPrecheckRequest(email);
  } catch (error) {
    const code = authErrorCode(error);
    if (
      code === "wrong-app-for-account" ||
      code === "not-an-internal-account"
    ) {
      throw error;
    }
    // Preserve LoginPage's fail-open behavior. The authenticated session
    // exchange remains the authoritative app-context gate.
  }
}

/**
 * Complete the shared Cognito → Penn session pipeline. Registration calls this
 * after its local user row exists; Login calls it after the ordinary email OTP.
 */
export async function completePennSession(args: {
  email: string;
  dispatch: AuthDispatch;
  navigate: NavigateFunction;
  returnTo?: string;
  signinPrechecked?: boolean;
}): Promise<void> {
  if (ENFORCE_WEB_APP_CONTEXT && args.signinPrechecked !== true) {
    await runSigninPrecheck(args.email);
  }

  if (ENFORCE_WEB_APP_CONTEXT && !(await inRequiredCognitoGroup())) {
    await cognitoSignOut();
    throw new Error(
      webCommonHost().wrongSurfaceMessage,
    );
  }

  const cognitoToken = await currentCognitoIdToken();
  const exchanged = await sessionExchangeRequest(cognitoToken);
  // The cookies themselves are set by the server and are unreadable/irrelevant
  // to us; only the CSRF value has to be echoed back on /refresh and /logout,
  // and the body copy is the reliable way to get it (ADR-0036).
  if (exchanged.csrfToken) setCsrfToken(exchanged.csrfToken);
  args.dispatch(
    setLogin({ accessToken: exchanged.accessToken, user: exchanged.user }),
  );

  try {
    const me = await fetchMe();
    args.dispatch(
      setLogin({
        accessToken: exchanged.accessToken,
        user: me.user,
        assumptions: me.assumptions,
      }),
    );
  } catch {
    // The exchange response carries the complete route-guard user shape.
  }

  args.navigate(args.returnTo ?? "/", { replace: true });
}
